import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { API, getActiveBackendUrl } from '../App';
import {
  Car,
  CreditCard,
  FileText,
  LogOut,
  Building2,
  DollarSign,
  Clock,
  CheckCircle,
  ChevronRight,
  Calendar,
  MapPin,
  User,
  Upload,
  Download,
  Eye,
  X,
  AlertCircle,
  TrendingUp,
  BarChart3,
  ChevronLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';


export default function PartnerDashboard() {
  const [partner, setPartner] = useState(null);
  const [activeTab, setActiveTab] = useState('rides');
  const [hiddenTabs, setHiddenTabs] = useState([]);

  useEffect(() => {
    axios.get(`${API}/hidden-sections`).then(r => {
      const h = r.data.partner || [];
      setHiddenTabs(h);
      setActiveTab(prev => (h.includes(prev) ? 'rides' : prev));
    }).catch(() => {});
  }, []);
  const [rides, setRides] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [selectedRides, setSelectedRides] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  
  // Get backend URL lazily to avoid initialization order issues
  const BACKEND_URL = useMemo(() => getActiveBackendUrl(), []);

  const token = localStorage.getItem('partner_token');

  useEffect(() => {
    if (!token) {
      navigate('/partner/login');
      return;
    }
    loadPartnerData();
  }, [token, navigate]);

  useEffect(() => {
    if (token) {
      loadMonthlySummary();
    }
  }, [selectedYear, token]);

  const loadPartnerData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };

      const [meRes, ridesRes, invoicesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/partner/me`, { headers }),
        axios.get(`${API}/partner/rides`, { headers }),
        axios.get(`${API}/partner/invoices`, { headers }),
        axios.get(`${API}/partner/summary`, { headers })
      ]);

      setPartner(meRes.data);
      setRides(ridesRes.data);
      setInvoices(invoicesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error loading partner data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('partner_token');
        localStorage.removeItem('partner_data');
        navigate('/partner/login');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlySummary = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/partner/summary/monthly?year=${selectedYear}`, { headers });
      setMonthlySummary(response.data);
    } catch (error) {
      console.error('Error loading monthly summary:', error);
    }
  };

  // Helper function to download invoice with authentication
  const handleDownloadInvoice = async (invoice) => {
    try {
      const response = await fetch(`${API}/partner/invoices/${invoice.id}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          toast.error('File della fattura non disponibile. Contatta l\'admin.');
          return;
        }
        throw new Error(`Download failed: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = invoice.file_name || 'invoice';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Error downloading invoice');
    }
  };

  // Helper function to preview invoice with authentication
  const handlePreviewInvoice = async (invoice) => {
    try {
      const response = await fetch(`${API}/partner/invoices/${invoice.id}/preview`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          toast.error('File della fattura non disponibile. Contatta l\'admin.');
          return;
        }
        throw new Error(`Preview failed: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Open in new tab for preview
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error previewing invoice:', error);
      toast.error('Error viewing invoice');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('partner_token');
    localStorage.removeItem('partner_data');
    navigate('/partner/login');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Active', variant: 'default', className: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'In Progress', variant: 'default', className: 'bg-yellow-100 text-yellow-800' },
      completed: { label: 'Completed', variant: 'default', className: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Cancelled', variant: 'default', className: 'bg-red-100 text-red-800' },
      paid_cancelled: { label: 'Cancelled', variant: 'default', className: 'bg-red-100 text-red-800' },
      free_cancelled: { label: 'Cancelled', variant: 'default', className: 'bg-red-100 text-red-800' },
      no_show: { label: 'No Show', variant: 'default', className: 'bg-orange-100 text-orange-800' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const unpaidRides = rides.filter(r => !r.is_paid && r.status === 'completed');
  const paidRides = rides.filter(r => r.is_paid);
  const ridesWithInvoice = unpaidRides.filter(r => r.partner_invoice_id);
  const ridesWithoutInvoice = unpaidRides.filter(r => !r.partner_invoice_id);

  const handleRideSelect = (rideId) => {
    setSelectedRides(prev =>
      prev.includes(rideId)
        ? prev.filter(id => id !== rideId)
        : [...prev, rideId]
    );
  };

  const handleSelectAllUnpaid = () => {
    if (selectedRides.length === unpaidRides.length) {
      setSelectedRides([]);
    } else {
      setSelectedRides(unpaidRides.map(r => r.id));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum 10MB allowed.');
        return;
      }
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['pdf', 'jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
        toast.error('Invalid file type. Allowed: PDF, JPG, PNG, WEBP');
        return;
      }
      setInvoiceFile(file);
    }
  };

  const handleUploadInvoice = async () => {
    if (selectedRides.length === 0) {
      toast.error('Please select at least one ride');
      return;
    }
    if (!invoiceFile) {
      toast.error('Please upload an invoice file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('ride_ids', JSON.stringify(selectedRides));
      formData.append('notes', paymentNotes);
      formData.append('invoice_file', invoiceFile);

      await axios.post(`${API}/partner/upload-invoice`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Invoice uploaded successfully! Admin will process your payment.');
      setShowPaymentModal(false);
      setSelectedRides([]);
      setPaymentNotes('');
      setInvoiceFile(null);
      loadPartnerData();
    } catch (error) {
      console.error('Error uploading invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload invoice');
    } finally {
      setUploading(false);
    }
  };

  // Helper to get payment amount. If payment_to_partner not set, treat as 0 (no fallback to ride price).
  const getPartnerAmount = (ride) => {
    return ride?.payment_to_partner || 0;
  };

  const selectedAmount = selectedRides.reduce((sum, id) => {
    const ride = unpaidRides.find(r => r.id === id);
    return sum + getPartnerAmount(ride);
  }, 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blue-50">
      {/* Header */}
      <header className="bg-black text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Building2 className="h-8 w-8" />
              <div>
                <h1 className="text-lg font-bold">Partner Portal</h1>
                <p className="text-xs text-blue-200">{partner?.name}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-white hover:bg-blue-900"
              data-testid="partner-logout-btn"
            >
              <LogOut className="h-5 w-5 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Summary Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-sm shadow p-4" data-testid="summary-total-rides">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Rides</p>
                <p className="text-2xl font-bold">{summary?.total_rides || 0}</p>
              </div>
              <Car className="h-8 w-8 text-sky-500" />
            </div>
          </div>
          <div className="bg-white rounded-sm shadow p-4" data-testid="summary-completed">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="text-2xl font-bold">{summary?.completed_rides || 0}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <div className="bg-white rounded-sm shadow p-4" data-testid="summary-unpaid">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Unpaid Amount</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(summary?.unpaid_amount)}</p>
              </div>
              <Clock className="h-8 w-8 text-orange-500" />
            </div>
          </div>
          <div className="bg-white rounded-sm shadow p-4" data-testid="summary-paid">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Paid Amount</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(summary?.paid_amount)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-sm shadow mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('rides')}
              className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
                activeTab === 'rides'
                  ? 'text-sky-500 border-b-2 border-sky-500 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-rides"
            >
              <Car className="h-5 w-5 inline mr-2" />
              Rides
            </button>
            {!hiddenTabs.includes('payments') && (
            <button
              onClick={() => setActiveTab('payments')}
              className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
                activeTab === 'payments'
                  ? 'text-sky-500 border-b-2 border-sky-500 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-payments"
            >
              <CreditCard className="h-5 w-5 inline mr-2" />
              Payments
            </button>
            )}
            {!hiddenTabs.includes('invoices') && (
            <button
              onClick={() => setActiveTab('invoices')}
              className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
                activeTab === 'invoices'
                  ? 'text-sky-500 border-b-2 border-sky-500 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-invoices"
            >
              <FileText className="h-5 w-5 inline mr-2" />
              Invoices ({invoices.length})
            </button>
            )}
            {!hiddenTabs.includes('summary') && (
            <button
              onClick={() => setActiveTab('summary')}
              className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
                activeTab === 'summary'
                  ? 'text-sky-500 border-b-2 border-sky-500 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-summary"
            >
              <BarChart3 className="h-5 w-5 inline mr-2" />
              Summary
            </button>
            )}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Rides Tab */}
            {activeTab === 'rides' && (
              <div data-testid="rides-content">
                <h3 className="text-lg font-semibold mb-4">All Rides ({rides.length})</h3>
                {rides.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Car className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No rides assigned yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {rides.map(ride => (
                      <div
                        key={ride.id}
                        className="border rounded-sm p-4 hover:bg-gray-50 transition-colors"
                        data-testid={`ride-card-${ride.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="font-medium">{formatDate(ride.date_time)}</span>
                              {getStatusBadge(ride.status)}
                              {ride.is_paid && (
                                <Badge className="bg-green-100 text-green-800">Paid</Badge>
                              )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                <span>{ride.customer_name || 'N/A'}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-green-600" />
                                <span className="truncate">{ride.pickup_location || 'N/A'}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-red-600" />
                                <span className="truncate">{ride.dropoff_location || 'N/A'}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <DollarSign className="h-4 w-4" />
                                <span className="font-semibold">{formatCurrency(getPartnerAmount(ride))}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && (
              <div data-testid="payments-content">
                {/* Unpaid Section */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Clock className="h-5 w-5 text-orange-500" />
                      To Be Paid ({unpaidRides.length})
                    </h3>
                    {unpaidRides.length > 0 && (
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={selectedRides.length === unpaidRides.length && unpaidRides.length > 0}
                            onCheckedChange={handleSelectAllUnpaid}
                          />
                          Select All
                        </label>
                        {selectedRides.length > 0 && (
                          <Button
                            onClick={() => setShowPaymentModal(true)}
                            className="bg-sky-500 hover:bg-sky-600"
                            data-testid="upload-invoice-btn"
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Upload Invoice ({selectedRides.length}) - {formatCurrency(selectedAmount)}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  {unpaidRides.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-sm">
                      <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                      <p>All completed rides have been paid!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {unpaidRides.map(ride => (
                        <div
                          key={ride.id}
                          className={`border rounded-sm p-4 cursor-pointer transition-colors ${
                            selectedRides.includes(ride.id)
                              ? 'border-blue-500 bg-blue-50'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => handleRideSelect(ride.id)}
                          data-testid={`unpaid-ride-${ride.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <Checkbox
                              checked={selectedRides.includes(ride.id)}
                              onCheckedChange={() => handleRideSelect(ride.id)}
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{formatDate(ride.date_time)}</span>
                                <span className="font-bold text-sky-500">{formatCurrency(getPartnerAmount(ride))}</span>
                              </div>
                              <div className="text-sm text-gray-500">
                                {ride.customer_name} • {ride.pickup_location} → {ride.dropoff_location}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Paid Section */}
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    Already Paid ({paidRides.length})
                  </h3>
                  {paidRides.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-sm">
                      <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No paid rides yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {paidRides.map(ride => (
                        <div
                          key={ride.id}
                          className="border rounded-sm p-4 bg-green-50 border-green-200"
                          data-testid={`paid-ride-${ride.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium">{formatDate(ride.date_time)}</span>
                              <div className="text-sm text-gray-500">
                                {ride.customer_name} • {ride.pickup_location} → {ride.dropoff_location}
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="font-bold text-green-600">{formatCurrency(getPartnerAmount(ride))}</span>
                              <div className="text-xs text-gray-500">
                                Paid: {formatDate(ride.payment_date)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Invoices Tab */}
            {activeTab === 'invoices' && (
              <div data-testid="invoices-content">
                <h3 className="text-lg font-semibold mb-4">Uploaded Invoices ({invoices.length})</h3>
                {invoices.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No invoices uploaded yet</p>
                    <p className="text-sm mt-2">Upload invoices when marking rides as paid</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {invoices.map(invoice => (
                      <div
                        key={invoice.id}
                        className="border rounded-sm p-4 hover:bg-gray-50 transition-colors"
                        data-testid={`invoice-card-${invoice.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="h-12 w-12 bg-sky-100 rounded-sm flex items-center justify-center">
                              <FileText className="h-6 w-6 text-sky-500" />
                            </div>
                            <div>
                              <p className="font-medium">{invoice.file_name}</p>
                              <p className="text-sm text-gray-500">
                                {invoice.ride_ids.length} ride(s) • {formatCurrency(invoice.total_amount)}
                              </p>
                              <p className="text-xs text-gray-400">
                                Uploaded: {formatDate(invoice.uploaded_at)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handlePreviewInvoice(invoice)}
                              className="p-2 hover:bg-gray-200 rounded-md transition-colors"
                              title="Preview"
                            >
                              <Eye className="h-5 w-5 text-gray-600" />
                            </button>
                            <button
                              onClick={() => handleDownloadInvoice(invoice)}
                              className="p-2 hover:bg-gray-200 rounded-md transition-colors"
                              title="Download"
                            >
                              <Download className="h-5 w-5 text-gray-600" />
                            </button>
                          </div>
                        </div>
                        {invoice.notes && (
                          <p className="text-sm text-gray-600 mt-2 pl-16">{invoice.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Summary Tab */}
            {activeTab === 'summary' && (
              <div data-testid="summary-content">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-sky-500" />
                    Monthly Payment Summary
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedYear(selectedYear - 1)}
                      data-testid="prev-year-btn"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Select
                      value={selectedYear.toString()}
                      onValueChange={(value) => setSelectedYear(parseInt(value))}
                    >
                      <SelectTrigger className="w-32" data-testid="year-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedYear(selectedYear + 1)}
                      disabled={selectedYear >= new Date().getFullYear()}
                      data-testid="next-year-btn"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Year Totals */}
                {monthlySummary && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-sm p-4 border border-blue-200">
                      <p className="text-sm text-gray-600">Total Amount ({selectedYear})</p>
                      <p className="text-2xl font-bold text-sky-600">{formatCurrency(monthlySummary.totals?.total_amount)}</p>
                      <p className="text-xs text-gray-500 mt-1">{monthlySummary.totals?.completed_rides || 0} completed rides</p>
                    </div>
                    <div className="bg-green-50 rounded-sm p-4 border border-green-200">
                      <p className="text-sm text-gray-600">Paid ({selectedYear})</p>
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(monthlySummary.totals?.paid_amount)}</p>
                      <p className="text-xs text-gray-500 mt-1">{monthlySummary.months?.reduce((sum, m) => sum + m.paid_count, 0) || 0} rides paid</p>
                    </div>
                    <div className="bg-orange-50 rounded-sm p-4 border border-orange-200">
                      <p className="text-sm text-gray-600">Unpaid ({selectedYear})</p>
                      <p className="text-2xl font-bold text-orange-600">{formatCurrency(monthlySummary.totals?.unpaid_amount)}</p>
                      <p className="text-xs text-gray-500 mt-1">{monthlySummary.months?.reduce((sum, m) => sum + m.unpaid_count, 0) || 0} rides unpaid</p>
                    </div>
                  </div>
                )}

                {/* Monthly Breakdown */}
                {!monthlySummary || monthlySummary.months?.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No data available for {selectedYear}</p>
                    <p className="text-sm mt-2">Rides will appear here once assigned to you</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {monthlySummary.months.map(month => (
                      <div
                        key={month.month_key}
                        className="border rounded-sm p-4 hover:bg-gray-50 transition-colors"
                        data-testid={`month-row-${month.month_key}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-lg">{month.month}</h4>
                            <p className="text-sm text-gray-500">
                              {month.completed_rides} completed ride{month.completed_rides !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold">{formatCurrency(month.total_amount)}</p>
                            <div className="flex gap-3 text-sm mt-1">
                              <span className="text-green-600">
                                <CheckCircle className="h-4 w-4 inline mr-1" />
                                {formatCurrency(month.paid_amount)} paid
                              </span>
                              {month.unpaid_amount > 0 && (
                                <span className="text-orange-600">
                                  <Clock className="h-4 w-4 inline mr-1" />
                                  {formatCurrency(month.unpaid_amount)} unpaid
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Progress bar */}
                        {month.total_amount > 0 && (
                          <div className="mt-3">
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-green-500 transition-all"
                                style={{ width: `${(month.paid_amount / month.total_amount) * 100}%` }}
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-1 text-right">
                              {Math.round((month.paid_amount / month.total_amount) * 100)}% paid
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invoice Upload Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-sky-500" />
              Upload Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-sm text-amber-800">
              <AlertCircle className="h-4 w-4 inline mr-2" />
              After uploading, admin will review and process your payment.
            </div>
            <div className="bg-blue-50 rounded-sm p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Selected Rides:</span>
                <span className="font-semibold">{selectedRides.length}</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-gray-600">Total Amount:</span>
                <span className="font-bold text-sky-500 text-lg">{formatCurrency(selectedAmount)}</span>
              </div>
            </div>

            <div>
              <Label htmlFor="invoice-file" className="text-sm font-medium">
                Invoice File *
              </Label>
              <div className="mt-2">
                <input
                  id="invoice-file"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-sky-600
                    hover:file:bg-sky-100
                    cursor-pointer"
                  data-testid="invoice-file-input"
                />
                {invoiceFile && (
                  <p className="text-sm text-sky-500 mt-2">
                    Selected: {invoiceFile.name}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Allowed: PDF, JPG, PNG, WEBP (max 10MB)
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="notes" className="text-sm font-medium">
                Notes (Optional)
              </Label>
              <Textarea
                id="notes"
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="Add any notes about this payment..."
                className="mt-2"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPaymentModal(false);
                setInvoiceFile(null);
                setPaymentNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUploadInvoice}
              disabled={uploading || !invoiceFile}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="confirm-upload-btn"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Invoice
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
